<template>
  <div class="h-100">
    <b-card class="h-100">
      <h3>Lead Status</h3>

      <!-- <b-row v-if="viewAs === 'admin'">
        <b-col cols="12">
          <v-select
            v-model="filterEmpId"
            :options="filterEmpIdOption"
            :reduce="(status) => status.id"
            label="name"
            placeholder=" Select Employee"
            class="mr-sm-1 mb-1 mb-sm-0 custom-font mt-2"
          >
            <template #option="data">
              <UserSelect :user="data" />
            </template>
          </v-select>
        </b-col>
      </b-row> -->

      <b-card-text class="h-100 mt-5">
        <div
          class="chart-container d-flex justify-content-center align-items-center"
        >
          <div v-if="isLoading" class="d-flex justify-content-center my-3">
            <b-spinner variant="primary" label="Loading..." />
          </div>
          <div
            v-else-if="statusData.length === 0 || !hasData"
            class="text-center my-5"
          >
            <p>No data available.</p>
          </div>
          <pie-chart
            v-else
            :chart-data="chartData"
            :options="chartOptions"
            :height="300"
            :selected-indexes="selectedStatusIndexes"
            @chart-click="handleChartClick"
          />
        </div>

        <!-- DataTable integrated below the chart -->
        <!-- <div v-if="selectedStatusIds.length > 0" class="mt-4 border-top pt-3">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h4 class="mb-0">Selected Leads: {{ selectedStatus }}</h4>
            <b-button
              variant="outline-secondary"
              size="sm"
              @click="selectedStatusIndexes = []"
            >
              Clear All
            </b-button>
          </div>
          <DataTable
            :key="selectedStatusIds.join(',') + '-' + filterEmpId"
            :show="5"
            :endpoint="leadEndpoint"
          />
        </div> -->
      </b-card-text>
    </b-card>

    <!-- Modal for detailed view -->
    <b-modal
      id="assignee-modal"
      centered
      :title="selectedStatus ? `Leads: ${selectedStatus}` : 'Leads by Status'"
      hide-footer
      size="xl"
      no-close-on-backdrop
      @hidden="closeModal"
    >
      <b-row>
        <!-- Left: Chart -->
        <b-col cols="12" md="6" class="mb-3">
          <div class="modal-chart d-flex justify-content-center">
            <pie-chart
              :chart-data="getModalChartData"
              :options="modalChartOptions"
              :height="400"
              :selected-indexes="selectedStatusIndexes"
              @chart-click="handleChartClick"
            />
          </div>
        </b-col>

        <!-- Right: Task List -->
        <b-col cols="12" md="6">
          <div class="modal-task-list">
            <div v-if="isLoading" class="d-flex justify-content-center my-3">
              <b-spinner variant="primary" label="Loading..." />
            </div>
            <div v-else-if="selectedStatusIndexes.length > 0">
              <DataTable
                :key="
                  selectedStatusIds.join(',') + '-' + filterEmpId + '-modal'
                "
                :show="5"
                :endpoint="leadEndpoint"
              />
            </div>
            <div v-else class="text-center my-5">
              <p>Select a status from the chart to see leads.</p>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-modal>
  </div>
</template>

<script>
// Import our custom PieChart component

// For Chart.js v2, we need to import and register controllers
import Chart from "chart.js";

// Import Bootstrap Vue components
import {
  BModal,
  BRow,
  BCol,
  BCard,
  BSpinner,
  BCardText,
  BCardTitle,
} from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { mapGetters } from "vuex";
import UserSelect from "@/layouts/components/UserSelect.vue";
import DataTable from "./DataTable.vue";
import PieChart from "./PieChart.vue";
// Register the controllers needed for the pie chart
Chart.defaults.global.defaultFontFamily =
  "'Montserrat', Helvetica, Arial, sans-serif";
Chart.defaults.global.defaultFontSize = 12;

export default {
  name: "LeadByStatusChart",
  components: {
    UserSelect,
    BCardText,
    "pie-chart": PieChart,
    DataTable,
    BModal,
    BRow,
    BCol,
    BCard,
    BCardTitle,
    BSpinner,
  },
  props: {
    viewAs: {
      type: String,
      default: "employee",
    },
    filterEmpId: {
      type: [Number, String],
      default: "",
    },
  },
  watch: {
    filterEmpId: _.debounce(function (newVal) {
      this.selectedStatusIndexes = [];
      this.loadLeadStatus();
    }, 300),
  },
  data() {
    return {
      statusData: [],
      users: [],
      isLoading: false,
      // Modal state
      showModal: false,
      selectedStatusIndexes: [], // changed from index to array to support multi-select

      // filterEmpId: "", // Removed as it is now a prop
      filterEmpIdOption: [],

      filterProjectIdOption: [],
      filterProjectId: "",

      hasData: false,

      filterBoardListMilestoneId: "",
      filterMilestoneBoardIdOption: [],
    };
  },
  computed: {
    ...mapGetters({
      authUser: "userModule/getUser",
    }),
    chartData() {
      // Check if statusData is empty
      if (this.statusData.length === 0) {
        console.log("chartData: statusData is empty");
        // Return a minimal valid chart data structure
        return {
          labels: ["No Data"],
          datasets: [
            {
              data: [1],
              backgroundColor: ["#cccccc"],
            },
          ],
        };
      }

      const labels = this.statusData.map((item) => item?.name || "Unknown");

      // Prefer API-provided color_code; fallback to generated palette
      const fallbackColors = this.getColors(this.statusData.length);
      const baseColors = this.statusData.map(
        (item, idx) => item.color || item.leadColorCode || fallbackColors[idx]
      );

      // Use leadCount for each status
      const data = this.statusData.map((item) => item.leadCount || 0);

      // Dim non-selected slices if a selection exists; otherwise keep all at full opacity
      const bgColors = baseColors.map((color, idx) =>
        this.withAlpha(
          color,
          this.selectedStatusIndexes.length > 0 &&
            !this.selectedStatusIndexes.includes(idx)
            ? 0.6
            : 1
        )
      );

      // Hover colors: darken base while keeping alpha at 1 for better contrast and consistency across formats
      const hoverColors = baseColors.map((color) =>
        this.darkenColor(this.withAlpha(color, 1), 15)
      );

      const borderColors = baseColors.map((color, idx) =>
        this.selectedStatusIndexes.includes(idx)
          ? "#2c3e50" // Dark blue-gray/black for selection
          : this.withAlpha(color, 1)
      );

      const borderTotalWidths = baseColors.map((_, idx) =>
        this.selectedStatusIndexes.includes(idx) ? 3 : 1
      );

      return {
        labels,
        datasets: [
          {
            data,
            backgroundColor: bgColors,
            hoverBackgroundColor: hoverColors,
            borderColor: borderColors,
            borderWidth: borderTotalWidths,
          },
        ],
      };
    },

    chartOptions() {
      // Simplified options for better compatibility
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: "bottom",
        },
        tooltips: {
          enabled: true,
        },
        animation: {
          animateScale: true,
          animateRotate: true,
        },
      };
    },
    modalChartOptions() {
      // Simplified options for better compatibility
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: "bottom",

          /* custom click behaviour */
          onClick(e, legendItem) {
            const chart = this.chart || this; // chart instance
            const meta = chart.getDatasetMeta(0); // first (and only) dataset
            const idx = legendItem.index; // data index that was clicked

            // add a flag on the chart the first time we run
            if (typeof chart.$firstLegendClick === "undefined") {
              chart.$firstLegendClick = true;
            }

            if (chart.$firstLegendClick) {
              /* ---------- FIRST CLICK ------------ */
              // show only the clicked slice/bar/point
              meta.data.forEach((el, i) => {
                el.hidden = i !== idx; // hide everything else
              });
              chart.$firstLegendClick = false; // switch to normal mode
            } else {
              /* ---------- SUBSEQUENT CLICKS ------- */
              // toggle just this one item
              const el = meta.data[idx];
              el.hidden = !el.hidden;
            }

            chart.update();
          },
        },
        tooltips: {
          enabled: true,
        },
        title: {
          display: true,
          text: "Leads by Status",
        },
        animation: {
          animateScale: true,
          animateRotate: true,
        },
      };
    },
    getModalChartData() {
      // Check if statusData is empty
      if (this.statusData.length === 0) {
        // Return a minimal valid chart data structure
        return {
          labels: ["No Data"],
          datasets: [
            {
              data: [1],
              backgroundColor: ["#cccccc"],
            },
          ],
        };
      }

      const labels = this.statusData.map((item) => item.name);
      const fallbackColors = this.getColors(this.statusData.length);
      const baseColors = this.statusData.map(
        (item, idx) => item.color || item.leadColorCode || fallbackColors[idx]
      );

      // Use leadCount for each status
      const data = this.statusData.map((item) => item.leadCount || 0);

      // Highlight the selected section with alpha handling for any color format
      const bgColors = baseColors.map((color, idx) =>
        this.withAlpha(
          color,
          this.selectedStatusIndexes.length > 0 &&
            !this.selectedStatusIndexes.includes(idx)
            ? 0.4 // Dimm unselected slices more in modal for contrast
            : 1
        )
      );

      // Hover colors: darken base while keeping alpha at 1 for better contrast
      const hoverColors = baseColors.map((color) =>
        this.darkenColor(this.withAlpha(color, 1), 15)
      );

      const borderColors = baseColors.map((color, idx) =>
        this.selectedStatusIndexes.includes(idx)
          ? "#2c3e50" // Dark selection border
          : this.withAlpha(color, 1)
      );

      const borderTotalWidths = baseColors.map((_, idx) =>
        this.selectedStatusIndexes.includes(idx) ? 4 : 1
      );

      return {
        labels,
        datasets: [
          {
            data,
            backgroundColor: bgColors,
            hoverBackgroundColor: hoverColors,
            borderColor: borderColors,
            borderWidth: borderTotalWidths,
          },
        ],
      };
    },

    // Computed properties for the modal
    selectedStatus() {
      if (this.selectedStatusIndexes.length === 0) return "";
      return this.selectedStatusIndexes
        .map((idx) => this.statusData[idx]?.name)
        .filter(Boolean)
        .join(", ");
    },
    selectedStatusIds() {
      if (this.selectedStatusIndexes.length === 0) return [];
      return this.selectedStatusIndexes
        .map((idx) => this.statusData[idx]?.id)
        .filter(Boolean);
    },

    leadEndpoint() {
      const base = "api/leads";
      const params = new URLSearchParams({
        include:
          "user,leadCategory,leadSource,leadStatus,department,activityLogs,leadCountry,dialCodeCountry,team,accountManager",
        view: this.viewAs,
        filterEmp: this.filterEmpId ?? "",
      });

      // Pass multiple status IDs
      this.selectedStatusIds.forEach((id) => {
        params.append("filterStatus[]", id);
      });

      return `${base}?${params.toString()}`;
    },
  },
  async created() {
    try {
      const users = await this.getUsers();

      this.filterEmpIdOption = (users?.data?.data || []).map((item) => ({
        avatar: item?.avatar,
        name: item?.name,
        email: item?.email,
        mobile: item?.mobile,
        employee_number: item?.employee_number,
        id: item?.id,
        departmentId: item?.department_id,
      }));

      await this.loadLeadStatus();
    } catch (error) {
      console.log(error);

      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Error",
          icon: "BellIcon",
          variant: "danger",
          text: error?.response?.data?.message || "Failed to load users",
        },
      });
    }
  },

  methods: {
    async getUsers() {
      return await this.$api.get("api/leads/assigned-users");
    },

    darkenColor(color, percent = 15) {
      // --- rgba(...) ---------------------------------------------------------
      if (color.startsWith("rgb")) {
        const match = color.match(
          /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/
        );
        if (!match) return color; // safety net

        let [, r, g, b, a] = match.map(Number);
        const delta = Math.round(255 * (percent / 100));

        r = Math.max(0, r - delta);
        g = Math.max(0, g - delta);
        b = Math.max(0, b - delta);

        // keep original alpha if supplied, else default to 1
        return `rgba(${r}, ${g}, ${b}, ${isNaN(a) ? 1 : a})`;
      }

      // --- #rrggbb -----------------------------------------------------------
      const num = parseInt(color.replace("#", ""), 16);
      if (!isNaN(num)) {
        let r = (num >> 16) - Math.round(255 * (percent / 100));
        let g = ((num >> 8) & 0x00ff) - Math.round(255 * (percent / 100));
        let b = (num & 0x0000ff) - Math.round(255 * (percent / 100));

        r = Math.max(0, r);
        g = Math.max(0, g);
        b = Math.max(0, b);

        return `#${((1 << 24) + (r << 16) + (g << 8) + b)
          .toString(16)
          .slice(1)}`;
      }

      // fallback
      return color;
    },

    // Ensure we can set alpha on any color format (#rrggbb, rgb/rgba, hsl/hsla)
    withAlpha(color, alpha = 1) {
      try {
        if (!color) return `rgba(0,0,0,${alpha})`;

        // Hex formats: #RRGGBB or #RGB
        if (color[0] === "#") {
          const { r, g, b } = this.hexToRgb(color);
          return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }

        // rgba or rgb
        if (color.startsWith("rgb")) {
          const match = color.match(
            /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/
          );
          if (!match) return color;
          const r = Number(match[1]);
          const g = Number(match[2]);
          const b = Number(match[3]);
          return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }

        // hsla or hsl - keep hue/sat/light and override/add alpha
        if (color.startsWith("hsl")) {
          const hsla = color
            .replace(/hsl\(/, "hsla(")
            .replace(/\)$/, `, ${alpha})`)
            .replace(
              /hsla\(([^,]+),\s*([^,]+),\s*([^,\)]+)\)/,
              `hsla($1, $2, $3, ${alpha})`
            );
          return hsla;
        }

        // Fallback: return as rgba black with provided alpha
        return `rgba(0,0,0,${alpha})`;
      } catch (e) {
        return color;
      }
    },

    hexToRgb(hex) {
      let c = hex.replace("#", "");
      if (c.length === 3) {
        c = c
          .split("")
          .map((ch) => ch + ch)
          .join("");
      }
      const num = parseInt(c, 16);
      return {
        r: (num >> 16) & 255,
        g: (num >> 8) & 255,
        b: num & 255,
      };
    },

    async loadLeadStatus() {
      // Load lead statuses and their counts
      try {
        this.isLoading = true;
        const params = {};
        params.view = this.viewAs;
        params.user = this.filterEmpId;
        const response = await this.$api.get("api/lead-dashboard/status", {
          params,
        });
        const list = response?.data?.data || [];
        // Map to a structure similar to leadStatusInfo in LeadView.vue, excluding the synthetic 'All' option
        this.statusData = list
          .filter(
            (item) =>
              item && item.name && item.id !== "" && item.leads_count > 0
          )
          .map((item) => ({
            id: item.id,
            name: item.name,
            leadCount: item.leads_count || 0,
            leadColorCode: item.color_code || null,
            color: item.color_code || null,
          }));

        this.hasData = this.statusData.some((item) => item.leadCount > 0);
      } catch (error) {
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    getColors(count) {
      if (count === 0) return [];
      const step = 360 / count;
      return Array.from(
        { length: count },
        (_, i) => `hsla(${i * step}, 70%, 60%, 0.8)`
      );
    },

    // Handle chart click event
    handleChartClick(eventData) {
      if (typeof eventData === "object" && eventData.source === "slice") {
        // Single select for slice clicks
        this.selectedStatusIndexes = [eventData.clickedIndex];
      } else if (
        typeof eventData === "object" &&
        eventData.source === "legend"
      ) {
        // Multi select for legend clicks
        this.selectedStatusIndexes = eventData.selectedSlices.map(
          (s) => s.index
        );
      } else if (Array.isArray(eventData)) {
        // Fallback for any other custom emissions
        this.selectedStatusIndexes = eventData.map((s) => s.index);
      }
      this.$bvModal.show("assignee-modal");
    },

    handleModalChartClick(eventData) {
      if (typeof eventData === "object" && eventData.source === "slice") {
        // Single select for slice clicks
        this.selectedStatusIndexes = [eventData.clickedIndex];
      } else if (
        typeof eventData === "object" &&
        eventData.source === "legend"
      ) {
        // Multi select for legend clicks
        this.selectedStatusIndexes = eventData.selectedSlices.map(
          (s) => s.index
        );
      } else if (Array.isArray(eventData)) {
        // Fallback
        this.selectedStatusIndexes = eventData.map((s) => s.index);
      }
    },

    // Close the modal
    closeModal() {
      this.showModal = false;
      this.selectedStatusIndexes = [];
    },
  },
};
</script>

<style scoped>
.task-by-assignee {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin: 20px 0;
  position: relative;
}

.chart-title {
  margin-top: 0;
  margin-bottom: 20px;
  color: #2c3e50;
  font-size: 1.2rem;
  font-weight: 600;
  text-align: center;
}

.chart-container {
  position: relative;
  width: 100%;
  min-height: 300px;
  cursor: pointer;
}

/* Modal styles */
.modal-chart {
  width: 100%;
  height: 100%;
  min-height: 400px;
}

.modal-task-list {
  flex: 1;
  border-left: 1px solid #e0e0e0;
  padding-left: 24px;
}

.modal-task-list h4 {
  margin-top: 0;
  margin-bottom: 16px;
  color: #2c3e50;
  font-size: 1.2rem;
}

.task-list {
  max-height: 400px;
  overflow-y: auto;
}

.task-item {
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  background-color: #f9f9f9;
  margin-bottom: 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.task-item:hover {
  background-color: #f0f0f0;
}

.task-item.selected-task {
  background-color: #e6f7ff;
  border-left: 3px solid #1890ff;
}

.task-status {
  font-weight: 500;
  color: #333;
}

.task-count {
  color: #666;
}

.no-tasks {
  padding: 16px;
  text-align: center;
  color: #666;
  font-style: italic;
}

@media (max-width: 768px) {
  .chart-container {
    min-height: 250px;
  }

  /* Bootstrap's b-row will automatically stack columns on small screens */
  .modal-task-list {
    border-left: none;
    border-top: 1px solid #e0e0e0;
    padding-top: 24px;
    margin-top: 24px;
  }
}
</style>
